/**
 * ZoneLabelOverlay — always-on, clickable area-code badges for placeable zones.
 *
 * One compact badge per zone, positioned at the zone center by projecting its
 * scene-feet origin to screen with `map.project()` on every camera move (RAF
 * coalesced — same imperative pattern as PlacementDebugOverlay). Clicking a badge
 * selects that zone (opening its placement config + easing the camera onto it).
 * Rendered across all right-rail views, so the codes are always visible.
 */
import { useEffect, useRef } from 'react'
import { CoordinateService } from '@/services/3d/CoordinateService'
import type { ZoneGeometry } from '../engine/types'
import type { ZoneLabelMode } from './ZoneLabelToggle'

interface Props {
  map: mapboxgl.Map | null
  /** Scene origin [lng, lat] for scene-feet → lng/lat conversion. */
  center: [number, number] | null
  zones: ZoneGeometry[]
  selectedZoneId: string | null
  /** visible (100%) · dimmed (20%) · hidden (not rendered). */
  mode: ZoneLabelMode
  onSelect: (areaId: string) => void
}

export function ZoneLabelOverlay({ map, center, zones, selectedZoneId, mode, onSelect }: Props) {
  const refs = useRef<Map<string, HTMLButtonElement | null>>(new Map())

  useEffect(() => {
    if (!map || !center || zones.length === 0 || mode === 'hidden') return

    const update = () => {
      for (const z of zones) {
        const el = refs.current.get(z.areaId)
        if (!el) continue
        const [lng, lat] = CoordinateService.scenePositionToLngLat([z.center[0], 0, z.center[1]], center)
        const p = map.project([lng, lat])
        el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate(-50%, -50%)`
      }
    }

    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    map.on('move', schedule)
    update()
    return () => {
      map.off('move', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [map, center, zones, mode])

  if (!map || !center || mode === 'hidden') return null

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[6] overflow-hidden transition-opacity duration-200"
      style={{ opacity: mode === 'dimmed' ? 0.2 : 1 }}
    >
      {zones.map((z) => {
        const selected = z.areaId === selectedZoneId
        return (
          <button
            key={z.areaId}
            type="button"
            ref={(el) => refs.current.set(z.areaId, el)}
            onClick={() => onSelect(z.areaId)}
            title={`${z.name} — configure placement`}
            className={`pointer-events-auto absolute left-0 top-0 whitespace-nowrap rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold leading-tight shadow-lg backdrop-blur-sm transition-colors ${
              selected
                ? 'border-sky-400 bg-sky-500/40 text-sky-50'
                : 'border-white/20 bg-black/70 text-white/80 hover:border-sky-400/60 hover:text-white'
            }`}
            style={{ transform: 'translate3d(-9999px,-9999px,0)', willChange: 'transform' }}
          >
            {z.code}
          </button>
        )
      })}
    </div>
  )
}
